import type { AdminKey, LogKeyChoice } from "./api";
import { personLabel } from "./personFilter";

export interface KeyOption {
  label: string;
  value: string;
}

/** A key's short name: the alias without its "owner:" prefix or rotation suffix, else the hash's tail. */
export function keyName(alias: string | null, id: string): string {
  return (alias ?? `…${id.slice(-8)}`).replace(/^[^:]*:/, "").replace(/~[a-z0-9]+$/, "");
}

/** Two keys can share a name (a rotated key, two laptops), so the hash's tail goes on every label. */
const labelOf = (alias: string | null, id: string) => `${keyName(alias, id)} · …${id.slice(-8)}`;

/** The Key choice on the request log (TAI-2570): personal keys, then service keys, each by name. */
export function keyChoiceGroups(choices: LogKeyChoice[]): { personal: KeyOption[]; service: KeyOption[] } {
  const opts = (kind: LogKeyChoice["kind"]) => choices.filter((c) => c.kind === kind)
    .map((c) => ({ value: c.id, label: labelOf(c.alias, c.id) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return { personal: opts("personal"), service: opts("service") };
}

/** Every key, for Administration › Requests, labelled with who answers for it. */
export function adminKeyOptions(keys: AdminKey[]): KeyOption[] {
  return keys.map((k) => {
    const who = k.ownerKind === "service" ? `service key${k.serviceOwner ? `, ${k.serviceOwner}` : ""}`
      : k.userId ? (k.userEmail ?? personLabel(k.userId)) : "no owner";
    return { value: k.id, label: `${labelOf(k.alias, k.id)} (${who})` };
  }).sort((a, b) => a.label.localeCompare(b.label));
}

